import { createColumnHelper } from "@tanstack/react-table";
import { EJOSRManagementColumns } from "../utils/types";
import { IconButton, Button, Stack } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import { useEJOESRApprovalContext } from "../../../Context/EJOESRApprovalContext";
import { useESRApprovalContext } from "../../../Context/ESRApprovalContext";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../../../../../components/AuthProvider";
import { useSnackBar } from "../../../../../../../components/ToastProvider";
import ImageStack from "../../../../../../../components/common/ImageStack";

export const useCreateSRColumns = (onPreview: (images: (string | File)[]) => void) => {
  const { showSnackBar } = useSnackBar();
  const currUser = useAuth();
  const hasESRManagementPermission =
    currUser &&
    currUser?.currentUserRole?.permissions.includes("ESR::MANAGEMENT");
  const navigate = useNavigate();

  const {
    setOpenDialog,
    setCurrentSRSelected,
    setSrActionType,
    setOpenConfirmDeleteSRDialog,
    currentEJOSelected,
    isViewOnly,
  } = useEJOESRApprovalContext();

  const { esrArray } = useESRApprovalContext();

  const { id } = useParams();

  const columnHelper = createColumnHelper<EJOSRManagementColumns>();

  const createESR = (id: any) => {
    navigate(`/esr-management/ejo-approved-and-esr-uncreated/create/${id}`, {
      state: { ejoData: currentEJOSelected },
    });
  };

  const ejoSRManagementColumns = [
    columnHelper.accessor("serviceRequestTypeId", {
      header: "",
      cell: () => null,
      meta: {
        hidden: true,
      },
    }),
    columnHelper.accessor("serviceRequestNo", {
      id: "serviceRequestNo",
      header: "No.",
      cell: (row) => row.getValue() ?? "-",
      enableGlobalFilter: true,
      enableColumnFilter: true,
    }),
    columnHelper.accessor("serviceRequest", {
      id: "serviceRequest",
      header: "Service Requested",
      cell: (row) => row.getValue(),
      enableGlobalFilter: true,
      enableColumnFilter: true,
    }),
    columnHelper.display({
      id: "remarks",
      header: "Remarks",
      cell: ({ row }) => {
        const remarks = row.original.remarks;
        const pmCode = row.original.pmCode;

        return remarks === null || remarks === "" ? pmCode : remarks;
      },
      enableGlobalFilter: true,
      enableColumnFilter: true,
    }),
    columnHelper.display({
      id: "Images",
      header: "Images",
      cell: ({ row }) => {
        const images = row.original.images;

        if (!images?.length) return "";

        return (
          <ImageStack
            images={images}
            sx={{ justifySelf: "center"}}
            onClick={() => onPreview(images)}
          />
        );
      },
      enableGlobalFilter: true,
      enableColumnFilter: true,
    }),
    ...(!isViewOnly
      ? [
          columnHelper.display({
            id: "actions",
            header: "Action",
            cell: ({ row }) => (
              <Stack direction="row" gap="2">
                <IconButton
                  color="primary"
                  onClick={() => {
                    const tempId = String(row.index);
                    setCurrentSRSelected({
                      id: row.original.id ? row.original.id : tempId,
                      pmCode: row.original.pmCode,
                      pmCodeId: row.original.pmCodeId,
                      remarks: row.original.remarks,
                      serviceRequest: row.original.serviceRequest,
                      serviceRequestTypeId: row.original.serviceRequestTypeId,
                      images: row.original.images,
                    });
                    setOpenDialog(true);
                    setSrActionType("update");
                  }}
                >
                  <EditIcon fontSize="medium" />
                </IconButton>

                <IconButton
                  color="primary"
                  onClick={() => {
                    const tempId = String(row.index);

                    setCurrentSRSelected({
                      id: tempId,
                      pmCode: row.original.pmCode,
                      pmCodeId: row.original.pmCodeId,
                      remarks: row.original.remarks,
                      serviceRequest: row.original.serviceRequest,
                      serviceRequestTypeId: row.original.serviceRequestTypeId,
                    });
                    setOpenConfirmDeleteSRDialog(true);
                  }}
                >
                  <DeleteIcon fontSize="medium" />
                </IconButton>
              </Stack>
            ),
            enableHiding: false,
          }),
        ]
      : []),
    ...(hasESRManagementPermission
      ? [
          columnHelper.display({
            id: "actions",
            header: "Action",
            cell: ({ row }) => (
              <Stack direction="row" gap="2">
                <Button
                  variant="text"
                  color={
                    esrArray.find((item) => item.id === row.original.id)
                      ? "success"
                      : "primary"
                  }
                  sx={{ textTransform: "none" }}
                  onClick={() => {
                    const isEsrCreated = esrArray.find(
                      (item) => item.id === row.original.id
                    );

                    // if (isEsrCreated) {
                    //   showSnackBar("JO has already been created", "warning");
                    //   return;
                    // }

                    setCurrentSRSelected({
                      id: row.original.id,
                      pmCode: row.original.pmCode,
                      pmCodeId: row.original.pmCodeId,
                      remarks: row.original.remarks,
                      serviceRequestNo: row.original.serviceRequestNo,
                      serviceRequest: row.original.serviceRequest,
                      serviceRequestTypeId: row.original.serviceRequestTypeId,
                    });
                    createESR(row.original.id);
                  }}
                >
                  <BuildCircleIcon fontSize="medium" />
                  {esrArray.find((item) => item.id === row.original.id)
                    ? "Created"
                    : "Create JO"}
                </Button>
              </Stack>
            ),
            enableHiding: false,
          }),
        ]
      : []),
  ];

  return ejoSRManagementColumns;
};
